import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { z } from 'zod';
import client from '../client';
import { isErrorEnvelope, parseEnvelope } from '../envelope';

const uuidSchema = z.string().uuid();
export { uuidSchema };
export const operationIdSchema = uuidSchema;
const positiveIntSchema = z.number().int().positive();
const moneySchema = z.string()
  .regex(/^-?(0|[1-9][0-9]{0,14})\.[0-9]{4}$/)
  .refine((value) => value !== '-0.0000', 'negative zero is not canonical');
const nonBlankStringSchema = z.string().refine((value) => value.trim().length > 0, 'must not be blank');

export const categoryAssignmentSchema = z.object({
  category_id: uuidSchema,
  taxonomy_version: nonBlankStringSchema,
}).strict();
export type CategoryAssignment = z.infer<typeof categoryAssignmentSchema>;

export const annotationChangesSchema = z.object({
  category: categoryAssignmentSchema.nullable().optional(),
  business_state: z.enum(['business', 'personal']).optional(),
}).strict().refine((value) => Object.keys(value).length > 0, 'at least one annotation change is required');
export type AnnotationChanges = z.infer<typeof annotationChangesSchema>;

export const annotationEditSchema = z.object({
  line_item_id: uuidSchema,
  expected_version: positiveIntSchema,
  changes: annotationChangesSchema,
}).strict();
export type AnnotationEdit = z.infer<typeof annotationEditSchema>;

export const lineItemPatchRequestSchema = z.object({
  operation_id: uuidSchema,
  expected_version: positiveIntSchema,
  changes: annotationChangesSchema,
}).strict();
export type LineItemPatchRequest = z.infer<typeof lineItemPatchRequestSchema>;

export const publicLineItemSchema = z.object({
  line_item_id: uuidSchema,
  transaction_id: uuidSchema,
  amount: moneySchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  category_id: uuidSchema.nullable(),
  category_slug: z.string().nullable(),
  category_name: z.string().nullable(),
  category_status: z.enum(['resolved', 'unlabeled', 'missing']),
  taxonomy_version: z.string(),
  is_business: z.boolean(),
  business_state: z.enum(['business', 'personal']),
  set_by: z.object({
    category: z.enum(['you', 'rule', 'suggestion', 'import']).nullable(),
    business: z.enum(['you', 'rule', 'suggestion', 'import']).nullable(),
  }).strict(),
  condition_id: z.object({ category: uuidSchema.nullable(), business: uuidSchema.nullable() }).strict(),
  version: positiveIntSchema,
  split_group_id: uuidSchema.nullable(),
  split_mode: z.enum(['amount', 'percent', 'categories', 'months']).nullable(),
  allocation_month: z.string().regex(/^[0-9]{4}-[0-9]{2}$/).nullable(),
  allocation_percent: moneySchema.nullable(),
}).strict().superRefine((item, context) => {
  if (item.business_state === 'business' && !item.is_business) {
    context.addIssue({ code: 'custom', path: ['business_state'], message: 'business state must match is_business' });
  }
  if (item.business_state === 'personal' && item.is_business) {
    context.addIssue({ code: 'custom', path: ['business_state'], message: 'personal state must match is_business' });
  }
  if (item.category_status === 'resolved' && (item.category_id === null || item.category_slug === null || item.category_name === null)) {
    context.addIssue({ code: 'custom', path: ['category_status'], message: 'resolved category must be complete' });
  }
  if (item.category_status === 'unlabeled' && (item.category_id !== null || item.category_slug !== null || item.category_name !== null)) {
    context.addIssue({ code: 'custom', path: ['category_status'], message: 'unlabeled category must be null' });
  }
  if (item.category_status === 'missing' && (item.category_id === null || item.category_slug !== null || item.category_name !== null)) {
    context.addIssue({ code: 'custom', path: ['category_status'], message: 'missing category must contain only its ID' });
  }
  if (item.allocation_percent === '-0.0000') {
    context.addIssue({ code: 'custom', path: ['allocation_percent'], message: 'negative zero is not canonical' });
  }
});
export type PublicLineItem = z.infer<typeof publicLineItemSchema>;

export const annotationMutationDataSchema = z.object({
  batch_id: uuidSchema,
  audit_ids: z.array(positiveIntSchema),
  applied_count: z.number().int().nonnegative(),
  unchanged_count: z.number().int().nonnegative(),
  line_items: z.array(publicLineItemSchema),
}).strict().superRefine((data, context) => {
  if (new Set(data.audit_ids).size !== data.audit_ids.length) {
    context.addIssue({ code: 'custom', path: ['audit_ids'], message: 'audit IDs must be unique' });
  }
  if (data.audit_ids.length !== data.line_items.length) {
    context.addIssue({ code: 'custom', path: ['line_items'], message: 'one audit is required per line item' });
  }
  if (data.applied_count + data.unchanged_count !== data.line_items.length) {
    context.addIssue({ code: 'custom', path: ['applied_count'], message: 'mutation counts must cover every line item' });
  }
});
export type AnnotationMutationData = z.infer<typeof annotationMutationDataSchema>;

export const annotationMutationMetaSchema = z.object({ idempotent_replay: z.boolean() }).strict();
export const annotationMutationResponseSchema = z.object({
  data: annotationMutationDataSchema,
  meta: annotationMutationMetaSchema,
}).strict();

export const bulkPreviewRequestSchema = z.object({
  edits: z.array(annotationEditSchema).min(1).max(200),
  scope_fingerprint: z.string().min(1),
}).strict().superRefine((request, context) => {
  const ids = request.edits.map((edit) => edit.line_item_id);
  if (new Set(ids).size !== ids.length) context.addIssue({ code: 'custom', path: ['edits'], message: 'line-item IDs must be unique' });
});
export type BulkPreviewRequest = z.infer<typeof bulkPreviewRequestSchema>;

const previewAnnotationSchema = z.object({
  category: categoryAssignmentSchema.nullable().optional(),
  business_state: z.enum(['business', 'personal']).optional(),
}).strict();
export const bulkPreviewEditSchema = z.object({
  line_item_id: uuidSchema,
  version: positiveIntSchema,
  merchant: z.string().nullable(),
  amount: moneySchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  current: previewAnnotationSchema,
  proposed: previewAnnotationSchema,
}).strict();
export const bulkPreviewResponseSchema = z.object({
  eligible_count: z.number().int().nonnegative(),
  unchanged_count: z.number().int().nonnegative(),
  edits: z.array(bulkPreviewEditSchema),
  expires_at: z.string().datetime({ offset: true }),
}).strict();
export type BulkPreviewResponse = z.infer<typeof bulkPreviewResponseSchema>;

export const bulkLineItemRequestSchema = z.object({
  operation_id: uuidSchema,
  edits: z.array(annotationEditSchema).min(1).max(200),
}).strict().superRefine((request, context) => {
  const ids = request.edits.map((edit) => edit.line_item_id);
  if (new Set(ids).size !== ids.length) context.addIssue({ code: 'custom', path: ['edits'], message: 'line-item IDs must be unique' });
});
export type BulkLineItemRequest = z.infer<typeof bulkLineItemRequestSchema>;

const changedSinceConflictDetailsSchema = z.object({
  reason: z.literal('changed_since'),
  conflicts: z.array(z.object({
    line_item_id: uuidSchema,
    expected_version: positiveIntSchema,
    current_version: positiveIntSchema,
    latest_audit_id: positiveIntSchema.nullable(),
  }).strict()),
}).strict();
const reasonDetails = (reason: 'idempotency_conflict' | 'nothing_to_undo' | 'already_undone') => z.object({ reason: z.literal(reason) }).strict();
export const changedSinceConflictSchema = changedSinceConflictDetailsSchema;
export type ChangedSinceConflict = z.infer<typeof changedSinceConflictDetailsSchema>;
export type MutationConflictCode = 'changed_since' | 'idempotency_conflict' | 'nothing_to_undo' | 'already_undone';
export type MutationConflict = {
  kind: 'conflict';
  code: MutationConflictCode;
  message: string;
  details: unknown;
};
export type MutationUnavailable = {
  kind: 'unavailable';
  reason: string;
  message: string;
};
export type MutationSuccess<T> = { kind: 'success'; idempotent_replay: false; data: T };
export type MutationReplay<T> = { kind: 'replay'; idempotent_replay: true; data: T };
export type MutationResult<T> = MutationSuccess<T> | MutationReplay<T> | MutationConflict | MutationUnavailable;
export type ApplyToSimilarOption = {
  merchant_id: string;
  merchant_display: string;
  this_month: number;
  all_time: number;
};

export class ApiError extends Error {
  readonly code: string;
  readonly details: unknown;

  constructor(code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
  }
}

function toApiError(error: unknown): ApiError {
  if (isAxiosError(error) && isErrorEnvelope(error.response?.data)) {
    const { code, message, details } = error.response.data.error;
    return new ApiError(code, message, details);
  }
  return new ApiError('unknown', error instanceof Error ? error.message : 'Request failed');
}

export function parseMutationFailure(error: unknown): MutationConflict | MutationUnavailable {
  const apiError = toApiError(error);
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status === 409) {
    const parsedDetails = apiError.code === 'changed_since'
      ? changedSinceConflictDetailsSchema.safeParse(apiError.details)
      : apiError.code === 'idempotency_conflict'
        ? reasonDetails('idempotency_conflict').safeParse(apiError.details)
        : apiError.code === 'nothing_to_undo'
          ? reasonDetails('nothing_to_undo').safeParse(apiError.details)
          : apiError.code === 'already_undone'
            ? reasonDetails('already_undone').safeParse(apiError.details)
            : null;
    if (parsedDetails?.success) {
      return { kind: 'conflict', code: apiError.code as MutationConflictCode, message: apiError.message, details: parsedDetails.data };
    }
    throw apiError;
  }
  if (status === undefined || status >= 500) {
    return { kind: 'unavailable', reason: apiError.code === 'unknown' ? 'request_failed' : apiError.code, message: apiError.message };
  }
  throw apiError;
}

function operationId(): string {
  const generated = globalThis.crypto?.randomUUID?.();
  if (!generated) throw new Error('operation ID generation is unavailable');
  return uuidSchema.parse(generated);
}

type LegacyPatchInput = { version: number; category_id?: string | null; is_business?: boolean };
export type LineItemPatchMutationInput =
  | { lineItemId: string; expectedVersion: number; changes: AnnotationChanges }
  | { lineItemId: string; input: LegacyPatchInput };

function requestFor(input: LineItemPatchMutationInput): { lineItemId: string; body: LineItemPatchRequest } {
  if ('input' in input) {
    if (input.input.category_id !== undefined && input.input.category_id !== null) {
      throw new ApiError('taxonomy_version_required', 'A category taxonomy version is required for this correction.');
    }
    const changes: AnnotationChanges = {
      ...('category_id' in input.input ? { category: input.input.category_id === undefined ? undefined : input.input.category_id === null ? null : undefined } : {}),
      ...('is_business' in input.input ? { business_state: input.input.is_business ? 'business' : 'personal' } : {}),
    };
    return { lineItemId: input.lineItemId, body: lineItemPatchRequestSchema.parse({ operation_id: operationId(), expected_version: input.input.version, changes }) };
  }
  return { lineItemId: input.lineItemId, body: lineItemPatchRequestSchema.parse({ operation_id: operationId(), expected_version: input.expectedVersion, changes: input.changes }) };
}

type LineItemPatchHookResult = MutationResult<AnnotationMutationData> & {
  /** Compatibility projection for the existing single-item correction controller. */
  line_item: PublicLineItem;
  /** The M2 PATCH contract has no apply-to-similar result. */
  apply_to_similar: ApplyToSimilarOption | null;
};

function withSingleItemCompatibility(result: MutationResult<AnnotationMutationData>): LineItemPatchHookResult {
  if (result.kind === 'success' || result.kind === 'replay') {
    const item = result.data.line_items[0];
    if (item) return { ...result, line_item: item, apply_to_similar: null };
  }
  return result as LineItemPatchHookResult;
}

function parseMutationResponse(payload: unknown): MutationResult<AnnotationMutationData> {
  const parsed = parseEnvelope(annotationMutationDataSchema, payload);
  const meta = annotationMutationMetaSchema.parse(parsed.meta ?? {});
  return meta.idempotent_replay
    ? { kind: 'replay', idempotent_replay: true, data: parsed.data }
    : { kind: 'success', idempotent_replay: false, data: parsed.data };
}

export function useLineItemPatch() {
  const queryClient = useQueryClient();
  return useMutation<LineItemPatchHookResult, ApiError, LineItemPatchMutationInput>({
    mutationFn: async (input) => {
      const { lineItemId, body } = requestFor(input);
      try {
        const response = await client.patch(`/api/line-items/${lineItemId}`, body);
        return withSingleItemCompatibility(parseMutationResponse(response.data));
      } catch (error) {
        if (isAxiosError(error)) {
          const failure = parseMutationFailure(error);
          // The old controller still supplies the legacy call shape and expects
          // its onError path for a stale version. New callers receive the typed
          // conflict result without throwing.
          if ('input' in input && failure.kind === 'conflict') {
            throw new ApiError(failure.code, failure.message, failure.details);
          }
          return withSingleItemCompatibility(failure);
        }
        throw error;
      }
    },
    retry: false,
    onSuccess: (result) => {
      if (result.kind === 'success' || result.kind === 'replay') {
        queryClient.invalidateQueries({ queryKey: ['transactions'] });
        queryClient.invalidateQueries({ queryKey: ['spend-by-category'] });
        queryClient.invalidateQueries({ queryKey: ['spend-by-merchant'] });
      }
    },
  });
}

export function useBulkLineItemPreview() {
  return useMutation<BulkPreviewResponse, ApiError, BulkPreviewRequest>({
    mutationFn: async (input) => {
      const body = bulkPreviewRequestSchema.parse(input);
      try {
        const response = await client.post('/api/line-items/bulk/preview', body);
        const parsed = parseEnvelope(bulkPreviewResponseSchema, response.data);
        z.object({ coverage: z.literal('complete') }).strict().parse(parsed.meta ?? {});
        return parsed.data;
      } catch (error) {
        if (isAxiosError(error)) throw toApiError(error);
        throw error;
      }
    },
    retry: false,
  });
}

export function useBulkLineItemPatch() {
  return useMutation<MutationResult<AnnotationMutationData>, ApiError, Omit<BulkLineItemRequest, 'operation_id'> & { operationId?: string }>({
    mutationFn: async (input) => {
      const body = bulkLineItemRequestSchema.parse({ ...input, operation_id: input.operationId ?? operationId() });
      try {
        const response = await client.post('/api/line-items/bulk', body);
        return parseMutationResponse(response.data);
      } catch (error) {
        if (isAxiosError(error)) return parseMutationFailure(error);
        throw error;
      }
    },
    retry: false,
  });
}

export function isChangedSinceError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.code === 'changed_since';
}
